import React from 'react';
import { colors, fonts, sectionLabelStyle } from './styles';

const S = { w: 22, h: 22, stroke: 1.75, fill: 'none', sw: 'round', lj: 'round' };
const IconFlask = () => (
  <svg width={S.w} height={S.h} viewBox="0 0 24 24" fill={S.fill} stroke="currentColor" strokeWidth={S.stroke} strokeLinecap={S.sw} strokeLinejoin={S.lj}>
    <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" />
    <path d="M7.5 14h9" />
  </svg>
);
const IconTrend = () => (
  <svg width={S.w} height={S.h} viewBox="0 0 24 24" fill={S.fill} stroke="currentColor" strokeWidth={S.stroke} strokeLinecap={S.sw} strokeLinejoin={S.lj}>
    <path d="M3 17l6-6 4 4 8-9" />
    <path d="M14 6h7v7" />
  </svg>
);
const IconGap = () => (
  <svg width={S.w} height={S.h} viewBox="0 0 24 24" fill={S.fill} stroke="currentColor" strokeWidth={S.stroke} strokeLinecap={S.sw} strokeLinejoin={S.lj}>
    <path d="M3 12h4M17 12h4" />
    <circle cx="9" cy="12" r="2" />
    <circle cx="15" cy="12" r="2" />
    <path d="M9 6l-2 3M15 6l2 3M9 18l-2-3M15 18l2-3" />
  </svg>
);
const IconClipboard = () => (
  <svg width={S.w} height={S.h} viewBox="0 0 24 24" fill={S.fill} stroke="currentColor" strokeWidth={S.stroke} strokeLinecap={S.sw} strokeLinejoin={S.lj}>
    <rect x="5" y="4" width="14" height="17" rx="2" />
    <path d="M9 3h6v3H9z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const IconQuote = () => (
  <svg width={S.w} height={S.h} viewBox="0 0 24 24" fill={S.fill} stroke="currentColor" strokeWidth={S.stroke} strokeLinecap={S.sw} strokeLinejoin={S.lj}>
    <path d="M6 11c0-3 2-5 5-5M6 11v6h5v-6H6zM13 11c0-3 2-5 5-5M13 11v6h5v-6h-5z" />
  </svg>
);
const IconCycle = () => (
  <svg width={S.w} height={S.h} viewBox="0 0 24 24" fill={S.fill} stroke="currentColor" strokeWidth={S.stroke} strokeLinecap={S.sw} strokeLinejoin={S.lj}>
    <path d="M4 12a8 8 0 0 1 14-5.3" />
    <path d="M20 12a8 8 0 0 1-14 5.3" />
    <path d="M18 3v4h-4M6 21v-4h4" />
  </svg>
);
const IconBolt = () => (
  <svg width={S.w} height={S.h} viewBox="0 0 24 24" fill={S.fill} stroke="currentColor" strokeWidth={S.stroke} strokeLinecap={S.sw} strokeLinejoin={S.lj}>
    <path d="M13 3L4 14h7l-1 7 9-11h-7l1-7z" />
  </svg>
);
const IconCompass = () => (
  <svg width={S.w} height={S.h} viewBox="0 0 24 24" fill={S.fill} stroke="currentColor" strokeWidth={S.stroke} strokeLinecap={S.sw} strokeLinejoin={S.lj}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" />
  </svg>
);

const useCases = [
  {
    icon: <IconFlask />,
    topic: 'Early-stage development',
    text: 'Pressure-test ideas before investing further',
    example: 'e.g. "How are patients currently managing this symptom, and what would make them consider a new option?"',
  },
  {
    icon: <IconTrend />,
    topic: 'Market intelligence',
    text: 'Understand how patients are responding to competitor launches or market shifts',
    example: 'e.g. "How are patients talking about the new entrant? What\'s changing in how they describe their treatment priorities?"',
  },
  {
    icon: <IconGap />,
    topic: 'Unmet needs',
    text: 'Identify gaps between disease stages or treatment milestones',
    example: 'e.g. "What would have made the diagnosis process more compassionate and manageable?"',
  },
  {
    icon: <IconClipboard />,
    topic: 'Clinical trial design',
    text: 'Inform protocol decisions and flag barriers before recruitment begins',
    example: 'e.g. "Which elements of this trial schedule are most likely to cause dropout in this patient population?"',
  },
  {
    icon: <IconQuote />,
    topic: 'Messaging & communications',
    text: 'Calibrate language against real patient priorities and vocabulary',
    example: 'e.g. "How do patients describe their condition to friends and family?"',
  },
  {
    icon: <IconCycle />,
    topic: 'Organizational continuity',
    text: 'Maintain patient intelligence during restructuring or team transitions',
    example: 'e.g. "I\'m new to working in this condition — what are the top five things patients wish people understood?"',
  },
  {
    icon: <IconBolt />,
    topic: 'Leadership questions',
    text: 'Answer urgent questions before the decision gets made without you',
    example: 'e.g. "Our CMO needs to know how patients feel about X by Thursday\'s strategy session."',
  },
  {
    icon: <IconCompass />,
    topic: 'Research & innovation prioritization',
    text: 'Determine where deeper or proprietary research is worth investing',
    example: 'e.g. "What unmet needs or innovations are patients most actively asking for that we haven\'t addressed yet?"',
  },
];

export const UseCasesSection = () => {
  return (
    <section
      style={{
        backgroundColor: colors.lightGray,
        padding: '96px 48px',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .pi-use-wrap { padding-left: 24px !important; padding-right: 24px !important; padding-top: 64px !important; padding-bottom: 64px !important; }
          .pi-use-grid { grid-template-columns: 1fr !important; }
        }
        .pi-use-item { transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s; }
        .pi-use-item:hover { border-color: ${colors.coralMid} !important; box-shadow: 0 8px 28px rgba(235,160,155,0.18) !important; transform: translateY(-2px); }
        .pi-use-icon { transition: background-color 0.2s; }
        .pi-use-item:hover .pi-use-icon { background-color: ${colors.coralLight} !important; }
      `}</style>
      <div className="pi-use-wrap" style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto 48px', textAlign: 'center' }}>
          <p style={{ ...sectionLabelStyle, textAlign: 'center' }}>Where teams use it</p>
          <h2
            style={{
              fontFamily: fonts.serif,
              fontSize: 'clamp(26px, 3vw, 40px)',
              fontWeight: 700,
              color: colors.charcoal,
              margin: '0 0 12px',
            }}
          >
            Across the product and trial lifecycle.
          </h2>
          <p
            style={{
              fontFamily: fonts.sans,
              fontSize: '17px',
              color: colors.warmGray,
              maxWidth: '620px',
              margin: '0 auto',
              fontWeight: 400,
              lineHeight: 1.65,
            }}
          >
            Clinical, commercial, medical affairs, digital health, and innovation teams.
            <br className="pi-br-md" />
            <span style={{ fontStyle: 'italic' }}>{' '}At any stage of development.</span>
          </p>
          <style>{`.pi-br-md { display: none; } @media (min-width: 768px) { .pi-br-md { display: inline; } }`}</style>
        </div>

        <div
          className="pi-use-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
          }}
        >
          {useCases.map((u) => (
            <div
              key={u.topic}
              className="pi-use-item"
              style={{
                backgroundColor: colors.white,
                padding: '24px 26px',
                borderRadius: '10px',
                border: `1px solid ${colors.border}`,
                display: 'flex',
                gap: '18px',
                alignItems: 'flex-start',
              }}
            >
              <div
                className="pi-use-icon"
                style={{
                  flex: '0 0 44px',
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(235,160,155,0.14)',
                  color: colors.deepCoral,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {u.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '1.2px',
                    color: colors.deepCoral,
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                  }}
                >
                  {u.topic}
                </div>
                <div
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: '15px',
                    color: colors.charcoal,
                    lineHeight: 1.5,
                    fontWeight: 500,
                    marginBottom: '6px',
                    textWrap: 'pretty',
                  }}
                >
                  {u.text}
                </div>
                <div
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: '13px',
                    color: colors.midGray,
                    lineHeight: 1.5,
                    fontWeight: 400,
                    fontStyle: 'italic',
                    textWrap: 'pretty',
                  }}
                >
                  {u.example}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
